// useItineraryState.js - Core state management for itinerary data
import { useState, useCallback, useEffect } from 'react';
import { generateId, generatePoiId, initialPoiState } from '../ItineraryCreator/utils/utils';

export const useItineraryState = (language = 'en-us') => {
  const initialItineraryState = {
    metadata: {
      id: generateId(),
      name: '',
      description: '',
      country: '',
      city: '',
      coordinates: { latitude: '', longitude: '' },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      language: language,
      tier: 'free',
      duration: '',
      difficulty: 'moderate',
      categories: [],
      tags: [],
      thumbnailUrl: '',
      thumbnailAttribution: null,
      authorId: '',
      authorName: '',
      rating: 0,
      reviewCount: 0,
      theme: '',
      isIntroductoryStop: true,
      ai_generated_personalization: false,
      b2bClient: false,
      feedbackCollect: false,
      translated_itinerary: 'no'
    },
    points: []
  };

  const initialItinerary = {
    metadata: {
      name: '',
      description: '',
      city: '',
      country: '',
      language: 'en-us',
      theme: '',
      duration: '',
      difficulty: '',
      categories: [],
      tags: [],
      thumbnailUrl: '',
      thumbnailAttribution: null,
      b2bClient: false,
      feedbackCollect: false,
      translated_itinerary: 'no',
      coordinates: {
        latitude: '',
        longitude: ''
      }
    },
    points: []
  };

  // Core itinerary state
  const [itinerary, setItinerary] = useState(initialItineraryState);
  const [currentPoi, setCurrentPoi] = useState(initialPoiState);
  const [contextFiles, setContextFiles] = useState([]);
  const [contextText, setContextText] = useState('');
  const [errors, setErrors] = useState({});

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalPoi, setOriginalPoi] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  // URL and database state
  const [publicUrl, setPublicUrl] = useState('');
  const [sourceGuideUrl, setSourceGuideUrl] = useState('');
  const [sqlCommand, setSqlCommand] = useState('');

  // B2B Client flag - NOT saved to JSON, only used for SQL tags
  const [b2bClient, setB2bClient] = useState(false);
  const metadata = itinerary.metadata;
  const metadataId = metadata.id;
  const metadataParentGuideId = metadata.parent_guide_id || '';
  const metadataLanguage = metadata.language || language;
  const isTranslatedItinerary = metadata.translated_itinerary === 'yes';

  useEffect(() => {
    setB2bClient(Boolean(metadata.b2bClient));
  }, [metadata.b2bClient]);

  useEffect(() => {
    if (!metadataId) {
      return;
    }

    if (!isTranslatedItinerary) {
      if (String(metadataParentGuideId) !== String(metadataId)) {
        setItinerary(prev => ({
          ...prev,
          metadata: {
            ...prev.metadata,
            parent_guide_id: metadataId
          }
        }));
      }

      setErrors(prev => {
        if (!prev.translation_parent) return prev;
        const { translation_parent, ...rest } = prev;
        return rest;
      });
      return;
    }

    if (!metadataParentGuideId) {
      setErrors(prev => ({
        ...prev,
        translation_parent: 'Translated guides must have a root Parent Guide ID.'
      }));
    } else {
      setErrors(prev => {
        if (!prev.translation_parent) return prev;
        const { translation_parent, ...rest } = prev;
        return rest;
      });
    }
  }, [
    metadataId,
    metadataParentGuideId,
    isTranslatedItinerary,
  ]);

  // Utility functions
  const resetToInitialState = () => {
    setItinerary(initialItinerary);
    setCurrentPoi(initialPoiState);
    setHasUnsavedChanges(false);
    setOriginalPoi(null);
    setIsUpdateMode(false);
    setErrors({});
    setB2bClient(false);
    setSourceGuideUrl('');
  };

  const updateMetadata = (updates) => {
    setItinerary(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        ...updates,
        updatedAt: new Date().toISOString()
      }
    }));
  };

  const addPoi = (poi) => {
    const newPoi = {
      ...poi,
      id: poi.id || generatePoiId(itinerary.metadata, itinerary.points),
      language: poi.language || language
    };

    setItinerary(prev => ({
      ...prev,
      points: [...prev.points, newPoi],
      metadata: {
        ...prev.metadata,
        updatedAt: new Date().toISOString()
      }
    }));
  };

  const updatePoi = (poiId, updates) => {
    setItinerary(prev => ({
      ...prev,
      points: prev.points.map(point =>
        point.id === poiId ? { ...point, ...updates } : point
      ),
      metadata: {
        ...prev.metadata,
        updatedAt: new Date().toISOString()
      }
    }));
  };

  const removePoi = (poiId) => {
    setItinerary(prev => ({
      ...prev,
      points: prev.points.filter(point => point.id !== poiId),
      metadata: {
        ...prev.metadata,
        updatedAt: new Date().toISOString()
      }
    }));
  };

  const reorderPois = (dragIndex, dropIndex) => {
    setItinerary(prev => {
      const newPoints = [...prev.points];
      const [draggedItem] = newPoints.splice(dragIndex, 1);
      newPoints.splice(dropIndex, 0, draggedItem);
      return {
        ...prev,
        points: newPoints,
        metadata: {
          ...prev.metadata,
          updatedAt: new Date().toISOString()
        }
      };
    });
  };

  // ID enforcement functions
  const checkPointIdFormat = useCallback((pointId, metadata) => {
    if (!pointId || !metadata) return false;

    // Get the expected guide identifier (parent_guide_id takes priority over metadata.id)
    const expectedGuideId = metadata.parent_guide_id || metadata.id;
    if (!expectedGuideId) return false;

    // Format: {guide_id}_{stop_sequence}_{language_code}
    // - guide_id: metadata.id or parent_guide_id
    // - stop_sequence: numeric sequence
    // - language_code: exactly 2 lowercase letters
    const pattern = new RegExp(`^${expectedGuideId}_\\d+_[a-z]{2}$`);
    return pattern.test(pointId);
  }, []);

  const regeneratePointIds = useCallback(() => {
    // Get the guide identifier (parent_guide_id takes priority over metadata.id)
    const guideId = metadataParentGuideId || metadataId;

    if (!guideId) {
      console.error('Cannot regenerate POI IDs: No metadata.id or parent_guide_id found');
      return;
    }

    // Extract only the first 2 characters for language code (e.g., "pt-pt" -> "pt", "en-US" -> "en")
    const fullLanguage = String(metadataLanguage || 'en').toLowerCase();
    const language = fullLanguage.split(/[-_]/)[0];

    // Generate new POI IDs following the format: {guide_id}_{stop_sequence}_{language_code}
    const newPoints = itinerary.points.map((point, index) => ({
      ...point,
      id: `${guideId}_${index + 1}_${language}`
    }));

    setItinerary(prev => ({
      ...prev,
      points: newPoints,
      metadata: {
        ...prev.metadata,
        updatedAt: new Date().toISOString()
      }
    }));

    // Clear any regeneration errors
    setErrors(prev => {
      const { regenerate, ...rest } = prev;
      return rest;
    });
  }, [metadataParentGuideId, metadataId, metadataLanguage, itinerary.points, setItinerary, setErrors]);

  // Function to automatically fix IDs that are in wrong format
  const fixInvalidPointIds = useCallback(() => {
    const invalidPoints = itinerary.points.filter(point => !checkPointIdFormat(point.id, metadata));

    if (invalidPoints.length === 0) {
      return false; // No invalid IDs found
    }

    console.log(`Found ${invalidPoints.length} invalid POI IDs that need fixing`);

    // Get the guide identifier (parent_guide_id takes priority over metadata.id)
    const guideId = metadataParentGuideId || metadataId;

    if (!guideId) {
      console.error('Cannot fix POI IDs: No metadata.id or parent_guide_id found');
      return false;
    }

    // Extract only the first 2 characters for language code
    const fullLanguage = String(metadataLanguage || 'en').toLowerCase();
    const language = fullLanguage.split(/[-_]/)[0];

    // Regenerate all POI IDs with proper format: {guide_id}_{stop_sequence}_{language_code}
    const fixedPoints = itinerary.points.map((point, index) => {
      const newId = `${guideId}_${index + 1}_${language}`;
      if (point.id !== newId) {
        console.log(`Fixed point ID: ${point.id} -> ${newId}`);
      }
      return { ...point, id: newId };
    });

    setItinerary(prev => ({
      ...prev,
      points: fixedPoints,
      metadata: {
        ...prev.metadata,
        updatedAt: new Date().toISOString()
      }
    }));

    return true; // IDs were fixed
  }, [itinerary.points, checkPointIdFormat, metadata, metadataId, metadataLanguage, metadataParentGuideId, setItinerary]);

  return {
    // State
    itinerary,
    setItinerary,
    currentPoi,
    setCurrentPoi,
    contextFiles,
    setContextFiles,
    contextText,
    setContextText,
    errors,
    setErrors,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    originalPoi,
    setOriginalPoi,
    isUpdateMode,
    setIsUpdateMode,
    publicUrl,
    setPublicUrl,
    sourceGuideUrl,
    setSourceGuideUrl,
    sqlCommand,
    setSqlCommand,
    b2bClient,
    setB2bClient,

    // Utility functions
    resetToInitialState,
    updateMetadata,
    addPoi,
    updatePoi,
    removePoi,
    reorderPois,
    checkPointIdFormat,
    regeneratePointIds,
    fixInvalidPointIds,

    // Constants
    initialItineraryState,
    initialItinerary
  };
};
